import _ from "lodash";
import moment from "moment";
import { CollectionAdapter } from "../../main/adapters/CollectionAdapter/CollectionAdapter";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";
import { SourcesService } from "../../presentation/services/SourcesService/SourcesService";

const ReportModel = new Mongo.Collection("Report", {
    connection: null
});

export class Report extends CollectionAdapter {
    HttpAdapter = new HttpAdapter();
    baseUrlReport = `${publicSettings.aws.baseUrls.dataReport}/report`;

    constructor() {
        super(ReportModel);
    }

    async populate(companyId) {
        const response = await this.getListByCompanyId(companyId);

        this.deleteMany({ companyId: { $eq: companyId } });

        const hasResponse = _.get(response, "length");

        if (hasResponse) this.createMany(response);

        return hasResponse ? true : false;
    }

    async getEngagementByAccountingId({ accountingId, date }) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/get-engagement-by-accountingid`,
            {
                accountingId,
                date: { year: date.year, month: date.month }
            }
        );

        return response;
    }

    async getListByCompanyId(companyId) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/get-by-companyid`,
            {
                companyId
            }
        );

        return response.response || [];
    }

    async getById({ _id }) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/get-by-id`,
            {
                _id
            }
        );
        const document = response?.response || {};
        document.tableValues = true;
        return document;
    }

    /**
     * @public
     * @param {Array} data
     * @returns {Boolean}
     */
    createMany(data = []) {
        this._before();

        const total = data.length;

        if (!total) return false;

        for (let i = 0; i < total; i++) {
            const document = data[i];

            if (document.updatedAt)
                document.updatedAt = new Date(document.updatedAt).getTime();

            if (document.createdAt)
                document.createdAt = new Date(document.createdAt).getTime();

            if (document.date)
                document.dateFormatted = moment(document.date)
                    .utcOffset(0)
                    .format("DD/MM/YYYY");

            this.create(document);
        }

        return true;
    }

    async insertOne(reportData) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/create`,
            {
                ...reportData
            }
        );

        this.create({ _id: response._id, ...reportData });

        return response._id;
    }

    /**
     * Cria o relatório de Funcionários (`type: "employees"`). Diferente do `insertOne`, não aceita
     * resposta sem `_id`: o `FetchClient` não lança em resposta que não é 2xx, e o 403 do back
     * (quem não pode compartilhar Funcionários) viraria um relatório fantasma na coleção local.
     * Lança `ReportNotCreatedError` com a `message` do back (vazia quando ele não mandou).
     */
    async insertEmployeesReport(reportData) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/create`,
            {
                ...reportData
            }
        );
        if (!response?._id) {
            // Só a recusa de acesso traz uma frase para o contador; um 500 traz texto técnico.
            const error = new Error(
                response?.name === "ForbiddenAccessError" && typeof response?.message === "string"
                    ? response.message
                    : ""
            );
            error.name = "ReportNotCreatedError";
            throw error;
        }

        this.create({ _id: response._id, ...reportData });

        return response._id;
    }

    async updateOne(_id, modifier) {
        if (!_id || _.isEmpty(modifier)) return false;

        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/update-one-by-id`,
            {
                _id,
                ...modifier
            }
        );

        this.modify({ _id: { $eq: _id } }, modifier);

        return response;
    }

    async removeByIds(reportIds) {
        if (!reportIds) return;

        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/remove-by-ids`,
            {
                ids: reportIds
            }
        );

        return response;
    }

    async remove(_id) {
        if (!_id) return;
        const response = await this.HttpAdapter.call(
            `${this.baseUrlReport}/remove-by-ids`,
            {
                ids: [_id]
            }
        );
        this.delete(_id);
        return response;
    }

    _getMainSource(source) {
        const sourcesService = new SourcesService();

        return sourcesService.getMainSource({ source });
    }

    async regenerateByReportId({ accountingId, reportIds, date }) {
        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrlReport}/regenerate-by-reportid`,
                {
                    accountingId,
                    reportIds,
                    date: {
                        year: date.year,
                        month: date.month
                    }
                }
            );

            return response;
        } catch (error) {
            console.log(error);
            return {};
        }
    }

    async getPeopleInteractByCompanyIdDate({ companyId, date }) {
        if (!companyId) return {};

        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrlReport}/get-people-interacted-by-id`,
                {
                    companyId,
                    date: {
                        year: date.year,
                        month: date.month
                    }
                }
            );

            return response;
        } catch (error) {
            console.log(error);
            return {};
        }
    }

    async getPeopleInteractById(_id) {
        if (!_id) return {};

        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrlReport}/get-people-interacted-by-id`,
                {
                    _id
                }
            );

            return response;
        } catch (error) {
            console.log(error);
            return {};
        }
    }

    async getKpisByAccountingIdDate(input) {
        if (!input.accountingId) return {};

        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrlReport}/get-kpis-by-accountingid-date`,
                input
            );

            return response;
        } catch (error) {
            console.log(error);
            return {};
        }
    }

    async shareReportById({
        optionsSharing,
        accountingId,
        userId,
        companyId,
        reportId,
        contacts
    }) {
        if (!reportId || !contacts || !contacts.length) return;

        try {
            const response = await this.HttpAdapter.call(
                `${this.baseUrlReport}/share-by-reportid`,
                {
                    optionsSharing,
                    accountingId,
                    userId,
                    companyId,
                    reportId,
                    contacts
                }
            );

            return response;
        } catch (error) {
            console.error(error);
            return {};
        }
    }
}
